import { defineStore } from "pinia";
import { ref, computed } from "vue";
import type { Good } from "@/entities/good";
import { getMerchantGoods } from "@/api/getMerchantGoods";
import { getNewId } from "@/utils/getNewId";
import { MarketplaceKey } from "@/marketplaces/registry";
import { saveGood } from "@/api/saveGood";
import { saveGoodVersion } from "@/api/saveGoodVersion";
import { CategoryData } from "./goodAddingWizard";
import { publicateGoodVersion } from "@/api/wbApi/publicateGoodVersion";
import { publicateKaspiGoodVersion } from "@/api/kaspiApi/publicateKaspiGoodVersion";
import { uploadImageToCrm } from "@/api/saveImage";

export const useGoodsStore = defineStore("goods", () => {
  const goods = ref<Good[]>([]);
  const initialized = ref(false);
  const loading = ref(false);

  const creatingGood = ref(false);

  async function createGood(
    marketplace: MarketplaceKey,
    contentType: "card" | "images" | "video",
    characteristics: { name: string; value: string }[],
    competitors: any[],
    images: string[],
    category: CategoryData,
    brand: string
  ) {
    const uploadedImages: string[] = [];

    for (const image of images) {
      const imageUrl = await uploadImageToCrm(image);

      uploadedImages.push(imageUrl);
    }

    const newGood: Good = {
      id: getNewId("good"),
      marketplace: marketplace,
      contentType,
      versions: [],
      publishStatus: "draft",
      characteristics: characteristics,
      competitors: competitors,
      images: uploadedImages,
      category: category,
      brand: brand,
      publicationData: {
        wb: {}
      }
    };

    const profile_id = await saveGood(newGood);

    newGood.profile_id = profile_id

    goods.value.unshift(newGood);

    return {
      profile_id,
      id: newGood.id,
      uploadedImages
    }
  }

  async function selectVersion(goodId: string, versionId: string) {
    try {

      const good = goods.value.find((g) => g.id === goodId);
      if (!good) {
        throw new Error("Good not found");
      }

      const versionToSelect = good.versions.find((v) => v.id === versionId);

      if (!versionToSelect) {
        throw new Error("Version not found");
      }

      creatingGood.value = true

      // Kaspi публикуется через наш backend (geni-backend), WB — напрямую из браузера.
      if (good.marketplace === "kaspi") {
        await publicateKaspiGoodVersion(good, versionToSelect)
      } else {
        // if (!good.publicationData.wb.id) {
        await publicateGoodVersion(good, versionToSelect)
        // }
      }

      const selectedVersion = good.versions.find((v) => v.isActive);


      if (selectedVersion?.profile_id) {
        if (selectedVersion.activePeriods?.length) {
          selectedVersion.activePeriods[selectedVersion.activePeriods.length - 1].end = new Date().toISOString();
        }
        selectedVersion.isActive = false;
        await saveGoodVersion(selectedVersion, goodId);
      }

      if (!versionToSelect.activePeriods) {
        versionToSelect.activePeriods = [];
      }
      versionToSelect.activePeriods.push({
        start: new Date().toISOString(),
        end: new Date().toISOString(),
      })
      versionToSelect.isActive = true;
      await saveGoodVersion(versionToSelect, goodId);

      creatingGood.value = false;
    } catch (e) {
      creatingGood.value = false;
      throw e;
    }
  }

  function addGood(good: Good) {
    goods.value.unshift(good);
  }

  function upsertGood(good: Good) {
    const existingGood = goods.value.find((item) => item.id === good.id);
    if (existingGood) {
      Object.assign(existingGood, good);
      return;
    }

    goods.value.unshift(good);
  }

  const goodsCount = computed(() => goods.value.length);

  let initPromise: Promise<void> | null = null;

  async function fetchGoodsIntoStore(options?: { silent?: boolean }) {
    const silent = options?.silent ?? false;
    if (!silent) {
      loading.value = true;
    }
    try {
      const fetchedGoods = await getMerchantGoods();
      const fetchedGoodsById = new Map(fetchedGoods.map((good) => [good.id, good]));
      const localOnlyGoods = goods.value.filter((good) => !fetchedGoodsById.has(good.id));

      // Keep optimistic local items that might not be returned by backend yet.
      goods.value = [...fetchedGoods, ...localOnlyGoods];
      initialized.value = true;
    } finally {
      if (!silent) {
        loading.value = false;
      }
    }

    // Подхватываем под фоновое наблюдение все товары, у которых идёт генерация
    // (после reload / открытия в новой вкладке). Идемпотентно (см. guard 1).
    try {
      const { useContentJobWatcher } = await import("./contentJobWatcher");
      useContentJobWatcher().scanAndResume();
    } catch (error) {
      console.warn("[goods] failed to resume content-job watchers", error);
    }
  }

  // actions
  async function init() {
    if (initialized.value) return;

    if (!initPromise) {
      initPromise = fetchGoodsIntoStore().finally(() => {
        initPromise = null;
      });
    }

    await initPromise;
  }

  async function refresh(options?: { silent?: boolean }) {
    if (initPromise) {
      await initPromise;
    }

    initPromise = fetchGoodsIntoStore(options).finally(() => {
      initPromise = null;
    });

    await initPromise;
  }

  function reset() {
    goods.value = [];
    initialized.value = false;
    initPromise = null;
  }

  return {
    goods,
    initialized,
    loading,
    addGood,
    upsertGood,
    goodsCount,
    init,
    refresh,
    reset,
    createGood,
    selectVersion,
    creatingGood
  };

});
